/**
 * Breadcrumbs components.
 * @module components/theme/Breadcrumbs/Breadcrumbs
 */

import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { Link } from 'react-router-dom';
import { Breadcrumb, Container, Segment } from 'semantic-ui-react';
import { defineMessages, injectIntl } from 'react-intl';

import { Icon } from '@plone/volto/components';
import { getBreadcrumbs } from '@plone/volto/actions';
import { getBaseUrl, hasApiExpander } from '@plone/volto/helpers';

import homeSVG from '@plone/volto/icons/home.svg';

const messages = defineMessages({
  home: {
    id: 'Home',
    defaultMessage: 'Home',
  },
  breadcrumbs: {
    id: 'Breadcrumbs',
    defaultMessage: 'Breadcrumbs',
  },
});

const normalizeBreadcrumbUrl = (url) => {
  if (!url) return '/';

  const withoutOrigin = url.replace(/^https?:\/\/[^/]+/i, '');
  const normalized = withoutOrigin
    .replace(/^\/?:8080(?=\/|$)/, '')
    .replace(/\/:8080(?=\/|$)/g, '');

  return normalized || '/';
};

const isHiddenBreadcrumb = (item) => {
  const itemUrl = normalizeBreadcrumbUrl(item.url);
  return itemUrl === '/articles' || item.title === '記事';
};

/**
 * Breadcrumbs container class.
 */
export class BreadcrumbsComponent extends Component {
  static propTypes = {
    getBreadcrumbs: PropTypes.func.isRequired,
    pathname: PropTypes.string.isRequired,
    root: PropTypes.string,
    items: PropTypes.arrayOf(
      PropTypes.shape({
        title: PropTypes.string,
        url: PropTypes.string,
      }),
    ).isRequired,
  };

  componentDidMount() {
    if (!hasApiExpander('breadcrumbs', getBaseUrl(this.props.pathname))) {
      this.props.getBreadcrumbs(getBaseUrl(this.props.pathname));
    }
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    if (nextProps.pathname !== this.props.pathname) {
      if (!hasApiExpander('breadcrumbs', getBaseUrl(this.props.pathname))) {
        this.props.getBreadcrumbs(getBaseUrl(nextProps.pathname));
      }
    }
  }

  render() {
    return (
      <Segment
        role="navigation"
        aria-label={this.props.intl.formatMessage(messages.breadcrumbs)}
        className="breadcrumbs"
        secondary
        vertical
      >
        <Container>
          <Breadcrumb>
            <Link
              to={normalizeBreadcrumbUrl(this.props.root)}
              className="section"
              title={this.props.intl.formatMessage(messages.home)}
            >
              <Icon name={homeSVG} size="18px" />
            </Link>
            {this.props.items.filter((item) => !isHiddenBreadcrumb(item)).map((item, index, items) => {
              const itemUrl = normalizeBreadcrumbUrl(item.url);
              return [
                <Breadcrumb.Divider key={`divider-${item.url}`} />,
                index < items.length - 1 ? (
                  <Link key={item.url} to={itemUrl} className="section">
                    {item.title}
                  </Link>
                ) : (
                  <Breadcrumb.Section key={item.url} active>
                    {item.title}
                  </Breadcrumb.Section>
                ),
              ];
            })}
          </Breadcrumb>
        </Container>
      </Segment>
    );
  }
}

export default compose(
  injectIntl,
  connect(
    (state) => ({
      items: state.breadcrumbs.items,
      root: state.breadcrumbs.root,
    }),
    { getBreadcrumbs },
  ),
)(BreadcrumbsComponent);
